import { create, type AxiosResponse } from 'axios';
import { API_ERROR_CAUSE_REASON } from '@shared/api/constants';
import { routesMap, safeEnv } from '@shared/lib';

import { AUTH_MFA_TYPE, AUTH_QUERY_KEY, AUTH_TENANT } from './constants';

import type {
  AuthState,
  AuthErrorResponse,
  AuthUser,
  PasswordResetConfirmBody,
  PasswordResetRequestBody,
  ResetPasswordBody,
  ServiceResponse,
  SignInBody,
  SignInChallenge,
  SignInMfaBody,
  SignInMfaSendBody,
  UpdateTokensBody,
} from './types';

let refreshPromise: Promise<boolean> | null = null;

const authClient = create({
  baseURL: `${safeEnv.VITE_AUTH_API}/api/v1`,
  withCredentials: true,
  validateStatus: () => true,
});

const withTenant = (params?: Record<string, string>) => ({
  tenant: AUTH_TENANT,
  ...params,
});

const parseString = (value: unknown) =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const getErrorCauseReason = (data: unknown) => {
  const error = toRecord(data) as AuthErrorResponse;
  const errorInfo = error.error_info;
  const metadata = errorInfo?.metadata;

  return (
    parseString(errorInfo?.reason) ??
    parseString(metadata?.cause_reason) ??
    parseString(metadata?.reason)
  );
};

const getMfaToken = (data: unknown) => {
  const body = toRecord(data);
  const errorInfo = toRecord(body.error_info);
  const metadata = toRecord(errorInfo.metadata);

  return (
    parseString(body.mfa_token) ??
    parseString(body.mfaToken) ??
    parseString(metadata.mfa_token) ??
    parseString(metadata.mfaToken)
  );
};

const getMfaType = (data: unknown) => {
  const body = toRecord(data);
  const errorInfo = toRecord(body.error_info);
  const metadata = toRecord(errorInfo.metadata);
  const mfaProps = Array.isArray(body.mfa_props)
    ? body.mfa_props
    : body.mfaProps;
  const firstMfaProp = toRecord(
    Array.isArray(mfaProps) ? mfaProps[0] : undefined,
  );

  return (
    parseString(body.mfa_type) ??
    parseString(body.mfaType) ??
    parseString(metadata.mfa_type) ??
    parseString(metadata.mfaType) ??
    parseString(firstMfaProp.mfa_type) ??
    parseString(firstMfaProp.mfaType) ??
    AUTH_MFA_TYPE
  );
};

const toServiceError = (
  response: AxiosResponse<unknown>,
): ServiceResponse<never> => {
  const causeReason = getErrorCauseReason(response.data);

  return {
    success: false,
    httpStatus: response.status,
    error:
      causeReason === API_ERROR_CAUSE_REASON.mfaPasscodeInvalid ||
      causeReason === API_ERROR_CAUSE_REASON.otpWrongCode
        ? 'Wrong verification code'
        : 'Wrong credentials data',
    causeReason,
  };
};

export const signInWithPassword = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<ServiceResponse<SignInChallenge>> => {
  try {
    const response = await authClient.post<
      unknown,
      AxiosResponse<unknown>,
      SignInBody
    >('/auth/signin', { email, password }, { params: withTenant() });

    if (response.status === 204) {
      return {
        success: true,
        httpStatus: response.status,
        data: { completed: true, mfaType: AUTH_MFA_TYPE },
      };
    }

    const mfaToken = getMfaToken(response.data);

    if (mfaToken) {
      return {
        success: true,
        httpStatus: response.status,
        data: {
          completed: false,
          mfaToken,
          mfaType: getMfaType(response.data),
        },
      };
    }

    return toServiceError(response);
  } catch {
    return { success: false, error: 'Failed to sign in' };
  }
};

export const sendSignInOtp = async ({
  mfaToken,
  mfaType,
}: {
  mfaToken: string;
  mfaType?: string;
}): Promise<ServiceResponse> => {
  try {
    const response = await authClient.post<
      unknown,
      AxiosResponse<unknown>,
      SignInMfaSendBody
    >(
      '/auth/signin/mfa/send',
      {
        mfa_token: mfaToken,
        mfa_type: mfaType ?? AUTH_MFA_TYPE,
      },
      { params: withTenant() },
    );

    if (response.status === 204) {
      return { success: true, httpStatus: response.status };
    }

    return toServiceError(response);
  } catch {
    return { success: false, error: 'Failed to send verification code' };
  }
};

export const verifySignInOtp = async ({
  mfaToken,
  mfaType,
  passcode,
}: {
  mfaToken: string;
  mfaType?: string;
  passcode: string;
}): Promise<ServiceResponse> => {
  try {
    const response = await authClient.post<
      unknown,
      AxiosResponse<unknown>,
      SignInMfaBody
    >(
      '/auth/signin/mfa',
      {
        mfa_token: mfaToken,
        mfa_type: mfaType ?? AUTH_MFA_TYPE,
        passcode,
      },
      { params: withTenant() },
    );

    if (response.status === 204) {
      return { success: true, httpStatus: response.status };
    }

    return toServiceError(response);
  } catch {
    return { success: false, error: 'Failed to verify code' };
  }
};

export async function waitForAuthSessionRefresh(): Promise<void> {
  await refreshPromise;
}

export const refreshAuthSession = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = authClient
    .put<unknown, AxiosResponse<unknown>, UpdateTokensBody>(
      routesMap.authTokens,
      {},
      { params: withTenant() },
    )
    .then((response) => response.status >= 200 && response.status < 300)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export const getAuthState = async (): Promise<AuthState> => {
  try {
    const authorize = () =>
      authClient.get<AuthUser>('/auth/authorize', { params: withTenant() });

    let response = await authorize();

    if (response.status === 401 && (await refreshAuthSession())) {
      response = await authorize();
    }

    if (response.status === 200) {
      return { user: response.data, httpStatus: response.status };
    }

    return { user: null, httpStatus: response.status };
  } catch {
    return { user: null, httpStatus: null };
  }
};

export const logout = async (): Promise<ServiceResponse> => {
  try {
    const response = await authClient.post('/auth/logout', undefined, {
      params: withTenant(),
    });

    if (response.status === 204) {
      return { success: true, httpStatus: response.status };
    }

    return toServiceError(response);
  } catch {
    return { success: false, error: 'Failed to log out' };
  }
};

export const sendPasswordResetEmail = async ({
  email,
}: {
  email: string;
}): Promise<ServiceResponse> => {
  try {
    const response = await authClient.post<
      unknown,
      AxiosResponse<unknown>,
      PasswordResetRequestBody
    >('/auth/password/reset/request', { email }, { params: withTenant() });

    if (response.status === 204) {
      return { success: true, httpStatus: response.status };
    }

    return toServiceError(response);
  } catch {
    return { success: false, error: 'Failed to reset password' };
  }
};

export const resetPassword = async ({
  email,
  code,
  newPassword,
}: {
  email: string;
  code?: string;
  newPassword?: string;
}): Promise<ServiceResponse> => {
  try {
    const payload: ResetPasswordBody = {
      email,
      ...(code ? { code } : {}),
      ...(newPassword ? { new_password: newPassword } : {}),
    };
    const response = await authClient.post<
      unknown,
      AxiosResponse<unknown>,
      ResetPasswordBody
    >('/auth/password/reset', payload, { params: withTenant() });

    if (response.status === 204) {
      return { success: true, httpStatus: response.status };
    }

    return toServiceError(response);
  } catch {
    return { success: false, error: 'Failed to reset password' };
  }
};

export const confirmPasswordReset = async ({
  token,
  newPassword,
}: {
  token: string;
  newPassword: string;
}): Promise<ServiceResponse> => {
  try {
    const payload: PasswordResetConfirmBody = {
      token,
      new_password: newPassword,
    };
    const response = await authClient.post<
      unknown,
      AxiosResponse<unknown>,
      PasswordResetConfirmBody
    >('/auth/password/reset/confirm', payload, { params: withTenant() });

    if (response.status === 204) {
      return { success: true, httpStatus: response.status };
    }

    if (
      getErrorCauseReason(response.data) ===
      API_ERROR_CAUSE_REASON.resetTokenInvalid
    ) {
      return {
        success: false,
        httpStatus: response.status,
        error:
          'This reset link is invalid or expired. Request a new password reset and try again.',
      };
    }

    return toServiceError(response);
  } catch {
    return { success: false, error: 'Failed to reset password' };
  }
};

export const getAuthQueryOptions = () => ({
  queryKey: [AUTH_QUERY_KEY],
  queryFn: getAuthState,
  staleTime: 10_000,
  retry: false,
});
